import * as React from "react";
import * as riskService from "../../../models/services/riskService";
import { ISessionState } from "source/models/session/sessionState";
import { RiskPageReportHtmlEmployeeRiskReport } from "./RiskPageReportHtmlEmployeeRiskReport";
import { PageTitle } from "source/components/framework/Page/PageTitle";
import { Translations } from "ui/Scripts/source/models/translations";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";
import { IRiskPageFilterLists, IRiskPageFilterState, RiskHtmlReportId } from "./RiskPageUtils";
import { IReportItem } from "source/models/report/reportItem";
import { RiskPageReportHtmlExtensiveRiskReport } from "./RiskPageReportHtmlExtensiveRiskReport";
import { SidePanelToggleButton } from "source/components/framework/Button/SidePanelToggleButton";
import { useEffect, useState } from "react";
import { getPrintBodyHtml } from "source/framework/viewToHtml";
import { AppUtils } from "source/framework/appUtils";
import { DownloadButton } from "source/components/framework/Button/DownloadButton/DownloadButton";
import { RiskPageReportHtmlTreatmentsRiskReport } from "./RiskPageReportHtmlTreatmentsRiskReport";
import { IRiskSettingEdit } from "source/models/riskSetting/riskSettingEdit";

interface IRiskPageReportHtmlProp extends IRiskPageFilterLists, IRiskPageFilterState {
    sessionState: ISessionState;
    companyId: string;
    report: IReportItem;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    showSidePanel: boolean;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
    onChangeShowSidePanel: (show: boolean) => void;
}

interface IRiskPageReportHtmlState {
    settings: IRiskSettingEdit;
    reportGenerated: boolean;
    dataId: string;
    isPrinting: boolean;
}

export const RiskPageReportHtml = (props: IRiskPageReportHtmlProp) => {
    const [state, setState] = useState<IRiskPageReportHtmlState>({
        settings: null,
        reportGenerated: false,
        dataId: "",
        isPrinting: false
    });

    useEffect(() => {
        const loadSettings = async () => {
            const settings = await AppUtils.callService(() => riskService.getRiskSettings(props.companyId), false);
            if (!settings) return;
            setState(old => ({
                ...old,
                settings,
            }));
        };

        loadSettings();
    }, [props.companyId]);

    const handleSetInitialized = (initialized: boolean) => {
        setState(old => ({
            ...old,
            reportGenerated: initialized,
        }));
    };

    const title = Translations.Reports + (props.report ? " - " + props.report.title : "");

    const handleDownload = async () => {
        setState(old => ({ ...old, isPrinting: true }));
        setTimeout(async () => {
            const html = getPrintBodyHtml("riskPrint");
            setState(old => ({ ...old, isPrinting: false }));
            if (!html) return;
            const fileName = await AppUtils.callService(() => riskService.getRiskReportPrint(props.companyId, props.report.title, html));
            if (!fileName) return;
            AppUtils.showSuccessMessage(String.format(Translations.PrintParameterDownloaded, fileName));
        }, 50);
    };

    if (!props.report) return null;

    return (
        <VerticalContainer
            before={
                <PageTitle title={title} position="absolute">
                    <>
                        <DownloadButton
                            sx={{ marginRight: "24px" }}
                            disabled={!state.reportGenerated}
                            onClick={handleDownload}
                        >
                            {Translations.DownloadPdf}
                        </DownloadButton>
                        <SidePanelToggleButton
                            right
                            value="preview"
                            selected={props.showSidePanel}
                            onChange={() => props.onChangeShowSidePanel(!props.showSidePanel)}
                            style={{ padding: "7px" }}
                        />
                    </>
                </PageTitle>
            }
            main={
                <MainListContainer>
                    {props.report.reportId === RiskHtmlReportId.EmployeeRiskReport && (
                        <RiskPageReportHtmlEmployeeRiskReport
                            companyId={props.companyId}
                            settings={state.settings}
                            isPrinting={state.isPrinting}
                            report={props.report}
                            riskTypes={props.riskTypes}
                            units={props.units}
                            states={props.states}
                            riskCategories={props.riskCategories}
                            riskProcesses={props.riskProcesses}
                            riskStandards={props.riskStandards}
                            riskStakeholders={props.riskStakeholders}
                            residualRisks={props.residualRisks}
                            riskTreatmentStates={props.riskTreatmentStates}
                            handlers={props.handlers}
                            owners={props.owners}
                            selectedRiskTypeIds={props.selectedRiskTypeIds}
                            selectedUnitIds={props.selectedUnitIds}
                            selectedStateIds={props.selectedStateIds}
                            selectedRiskCategoryIds={props.selectedRiskCategoryIds}
                            selectedRiskProcessIds={props.selectedRiskProcessIds}
                            selectedRiskStandardIds={props.selectedRiskStandardIds}
                            selectedRiskStakeholderIds={props.selectedRiskStakeholderIds}
                            selectedResidualRiskIds={props.selectedResidualRiskIds}
                            selectedRiskTreatmentStateIds={props.selectedRiskTreatmentStateIds}
                            selectedHandlerUserCodes={props.selectedHandlerUserCodes}
                            selectedOwners={props.selectedOwners}
                            dataId={state.dataId}
                            sortColumn={props.sortColumn}
                            sortOrderIsAsc={props.sortOrderIsAsc}
                            onSetInitialized={handleSetInitialized}
                            onSetSortData={props.onSetSortData}
                        />
                    )}
                    {props.report.reportId === RiskHtmlReportId.ExtensiveRiskReport && (
                        <RiskPageReportHtmlExtensiveRiskReport
                            sessionState={props.sessionState}
                            companyId={props.companyId}
                            settings={state.settings}
                            isPrinting={state.isPrinting}
                            report={props.report}
                            riskTypes={props.riskTypes}
                            units={props.units}
                            states={props.states}
                            riskCategories={props.riskCategories}
                            riskProcesses={props.riskProcesses}
                            riskStandards={props.riskStandards}
                            riskStakeholders={props.riskStakeholders}
                            residualRisks={props.residualRisks}
                            riskTreatmentStates={props.riskTreatmentStates}
                            handlers={props.handlers}
                            owners={props.owners}
                            selectedRiskTypeIds={props.selectedRiskTypeIds}
                            selectedUnitIds={props.selectedUnitIds}
                            selectedStateIds={props.selectedStateIds}
                            selectedRiskCategoryIds={props.selectedRiskCategoryIds}
                            selectedRiskProcessIds={props.selectedRiskProcessIds}
                            selectedRiskStandardIds={props.selectedRiskStandardIds}
                            selectedRiskStakeholderIds={props.selectedRiskStakeholderIds}
                            selectedResidualRiskIds={props.selectedResidualRiskIds}
                            selectedRiskTreatmentStateIds={props.selectedRiskTreatmentStateIds}
                            selectedHandlerUserCodes={props.selectedHandlerUserCodes}
                            selectedOwners={props.selectedOwners}
                            dataId={state.dataId}
                            sortColumn={props.sortColumn}
                            sortOrderIsAsc={props.sortOrderIsAsc}
                            onSetInitialized={handleSetInitialized}
                            onSetSortData={props.onSetSortData}
                        />
                    )}
                    {props.report.reportId === RiskHtmlReportId.TreatmentsRiskReport && (
                        <RiskPageReportHtmlTreatmentsRiskReport
                            sessionState={props.sessionState}
                            companyId={props.companyId}
                            settings={state.settings}
                            isPrinting={state.isPrinting}
                            report={props.report}
                            riskTypes={props.riskTypes}
                            units={props.units}
                            states={props.states}
                            riskCategories={props.riskCategories}
                            riskProcesses={props.riskProcesses}
                            riskStandards={props.riskStandards}
                            riskStakeholders={props.riskStakeholders}
                            residualRisks={props.residualRisks}
                            riskTreatmentStates={props.riskTreatmentStates}
                            handlers={props.handlers}
                            owners={props.owners}
                            selectedRiskTypeIds={props.selectedRiskTypeIds}
                            selectedUnitIds={props.selectedUnitIds}
                            selectedStateIds={props.selectedStateIds}
                            selectedRiskCategoryIds={props.selectedRiskCategoryIds}
                            selectedRiskProcessIds={props.selectedRiskProcessIds}
                            selectedRiskStandardIds={props.selectedRiskStandardIds}
                            selectedRiskStakeholderIds={props.selectedRiskStakeholderIds}
                            selectedResidualRiskIds={props.selectedResidualRiskIds}
                            selectedRiskTreatmentStateIds={props.selectedRiskTreatmentStateIds}
                            selectedHandlerUserCodes={props.selectedHandlerUserCodes}
                            selectedOwners={props.selectedOwners}
                            dataId={state.dataId}
                            sortColumn={props.sortColumn}
                            sortOrderIsAsc={props.sortOrderIsAsc}
                            onSetInitialized={handleSetInitialized}
                            onSetSortData={props.onSetSortData}
                        />
                    )}
                </MainListContainer>
            }
        />
    );
};
